import { PALETTE, type Presenter, type PresenterIcon } from '@micplot/shared';

/** Simple glyphs, readable at 28px in a dark room. */
const ICON_GLYPH: Record<PresenterIcon, string> = {
  star: '★', mic: '🎤', guitar: '🎸', keys: '🎹', drums: '🥁', horn: '🎺',
  violin: '🎻', voice: '🗣', crown: '♔', mask: '🎭', podium: '🎙', note: '♪',
};

interface Props {
  presenter: Presenter;
  size?: number;
  /** On a colour-flooded card the ring should read against the fill. */
  onFill?: boolean;
}

/**
 * Photo if there is one, else a chosen icon, else the presenter's initials.
 *
 * Three tiers because there are three realities: a headshot supplied in
 * advance, someone grabbed in the corridor five minutes before doors, and a
 * name typed in with no time for either.
 */
export default function Avatar({ presenter, size = 30, onFill }: Props) {
  const colour = PALETTE[presenter.color % PALETTE.length];
  const style: React.CSSProperties = {
    width: size, height: size, fontSize: Math.round(size * 0.42),
  };

  if (presenter.headshotUrl) {
    return (
      <img
        className={`avatar${onFill ? ' on-fill' : ''}`}
        style={style}
        src={presenter.headshotUrl}
        alt=""
        aria-hidden="true"
      />
    );
  }

  const glyph = presenter.icon ? ICON_GLYPH[presenter.icon] : initials(presenter.name);
  return (
    <span
      className={`avatar avatar-glyph${onFill ? ' on-fill' : ''}`}
      style={{ ...style, background: onFill ? 'rgba(0,0,0,0.22)' : colour }}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
}

export { ICON_GLYPH };

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts.at(-1)![0]!).toUpperCase();
}
